import React, { useEffect, useState } from 'react';
import cx from 'utils/classnames';
import { LogDebug } from 'utils/logging';
import useTokenReplacement from 'hooks/custom/useTokenReplacement';
import MultiStepTitle from './MultiStepTitle';

interface MultiStepLoaderProps {
  duration: number; // Duration in milliseconds for the entire animation sequence
  isProcessing: boolean;
  onLoaderComplete: () => void;
  onLoaderStart: () => void;
}

interface ListItemState {
  id: number;
  title: string;
  visible: boolean;
  progress: number;
}

export function MultiStepLoader(
  props: MultiStepLoaderProps
): React.JSX.Element {
  const {
    duration = 1500,
    isProcessing,
    onLoaderComplete,
    onLoaderStart,
  } = props;
  const [listItems, setListItems] = useState<ListItemState[]>([
    {
      id: 1,
      title: 'Searching schools that offer {SUBJECT} programs',
      visible: false,
      progress: 0,
    },
    {
      id: 2,
      title: 'Matching with your experience',
      visible: false,
      progress: 0,
    },
    {
      id: 3,
      title: 'Checking program availability',
      visible: false,
      progress: 0,
    },
  ]);
  const intervalDuration = duration / listItems.length;
  const isProcessingRef = React.useRef(isProcessing);
  const defaultProgressIncrement = 10;
  const progressIncrement = React.useRef(defaultProgressIncrement);
  const cssDelayPerStep = 600;

  const tokenizedTitle = useTokenReplacement(
    'Alright {FIRST_NAME}, let me find your matching schools...'
  );

  // Update the ref when the prop changes
  useEffect(() => {
    isProcessingRef.current = isProcessing;
  }, [isProcessing]);

  function getIntervalDuration(
    isLastStep: boolean,
    currentProgress: number
  ): number {
    if (isProcessingRef.current && isLastStep) {
      if (currentProgress >= 80) {
        progressIncrement.current = 1;
        return 3000;
      }

      if (currentProgress >= 70) {
        return 3000;
      }

      if (currentProgress >= 60) {
        progressIncrement.current = 2.5;
        return 2500;
      }

      if (currentProgress >= 20) {
        progressIncrement.current = 5;
        return 1500;
      }
    }

    if (progressIncrement.current !== defaultProgressIncrement) {
      progressIncrement.current = defaultProgressIncrement;
    }

    return intervalDuration / progressIncrement.current;
  }

  function startAnimation(
    currentItemIndex: number,
    currentProgress: number
  ): Promise<void> {
    if (currentItemIndex >= listItems.length) {
      return Promise.resolve();
    }

    const isLastStep = currentItemIndex === listItems.length - 1;

    // Show the next item
    if (!listItems[currentItemIndex].visible) {
      setListItems((prevItems) => {
        const newItems = [...prevItems];
        newItems[currentItemIndex].visible = true;
        return newItems;
      });
    }

    return new Promise((resolve) => {
      const progress = Math.min(
        currentProgress + progressIncrement.current,
        100
      );
      const isStepComplete = progress >= 100;

      setTimeout(() => {
        setListItems((prevItems) => {
          const newItems = [...prevItems];
          newItems[currentItemIndex] = {
            ...newItems[currentItemIndex],
            progress,
          };
          return newItems;
        });

        startAnimation(
          isStepComplete ? currentItemIndex + 1 : currentItemIndex,
          isStepComplete ? 0 : progress
        ).then(() => {
          resolve();
        });
      }, getIntervalDuration(isLastStep, progress)); // Progress steps of 10% at each interval (slowed down for last item if conditions are met)
    });
  }

  useEffect(() => {
    onLoaderStart();
    startAnimation(/* currentItemIndex */ 0, /* currentProgress */ 0).then(
      () => {
        setTimeout(() => {
          onLoaderComplete();

          if (isProcessingRef.current) {
            // This means the API took way to long to resolve or failed
            LogDebug('MultiStepLoader: Loading is complete with no results');
          }
        }, cssDelayPerStep);
      }
    );
  }, [duration]);

  return (
    <div className="multiStepLoader">
      <div className="multiStepLoader__title">{tokenizedTitle}</div>
      <ul className="multiStepLoader__items">
        {listItems.map((item) => {
          const multiStepLoaderItemClass = cx('multiStepLoader__item', {
            'multiStepLoader__item--isVisible': item.visible,
            'multiStepLoader__item--isComplete': item.progress === 100,
          });
          return (
            <li
              key={item.id}
              className={multiStepLoaderItemClass}
              style={
                {
                  '--multiStepLoaderProgress': `${item.progress}%`,
                } as React.CSSProperties
              }
            >
              <MultiStepTitle title={item.title} />
              <div className="multiStepLoader__itemProgress">
                <span className="multiStepLoader__progressText">
                  {item.progress}%
                </span>
              </div>
              <div className="multiStepLoader__itemIcon" />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
